// Use the REST URL localized by wp_localize_script so non-default WP REST
// prefixes (e.g. sites that change /wp-json/) are handled correctly.
import type { CartResponse } from '../types/cart';
import type { CheckoutRequest, CheckoutResponse } from '../types/checkout';

const STORE_API_BASE = `${window.altcheckoutConfig?.restUrl ?? '/wp-json/'}wc/store/v1`;

let cartToken: string | null = null;
let chosenPaymentMethod: string | null = null;

/** Set the chosen payment method — included as a header on Store API requests
 *  so the PHP fee-calculation hook can read it before fees are computed. */
export function setChosenPaymentMethod(method: string): void {
    chosenPaymentMethod = method;
}

async function fetchWithCartToken(
    endpoint: string,
    options: RequestInit = {}
): Promise<Response> {
    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Nonce': window.altcheckoutConfig?.nonce ?? '',
        ...(cartToken ? { 'Cart-Token': cartToken } : {}),
        ...(chosenPaymentMethod ? { 'X-AltCheckout-Payment-Method': chosenPaymentMethod } : {}),
        ...(options.headers ?? {}),
    };

    const response = await fetch(`${STORE_API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers,
    });

    const tokenFromHeader = response.headers.get('Cart-Token');
    if (tokenFromHeader && !cartToken) {
        cartToken = tokenFromHeader;
    }

    return response;
}

export async function getCart(): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart');
    if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
    }
    return response.json();
}

export async function removeItem(key: string): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart/remove-item', {
        method: 'POST',
        body: JSON.stringify({ key }),
    });
    if (!response.ok) throw new Error(`Remove item failed: ${response.status}`);
    return response.json();
}

export async function addItem(productId: number, quantity = 1): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart/add-item', {
        method: 'POST',
        body: JSON.stringify({ id: productId, quantity }),
    });
    if (!response.ok) throw new Error(`Add item failed: ${response.status}`);
    return response.json();
}

export async function applyCoupon(code: string): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart/apply-coupon', {
        method: 'POST',
        body: JSON.stringify({ code }),
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error((error as { message?: string })?.message ?? 'Coupon code is not valid.');
    }
    return response.json();
}

export async function removeCoupon(code: string): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart/remove-coupon', {
        method: 'POST',
        body: JSON.stringify({ code }),
    });
    if (!response.ok) throw new Error(`Remove coupon failed: ${response.status}`);
    return response.json();
}

export async function updateCustomer(data: {
    billing_address?: Record<string, string>;
}): Promise<CartResponse> {
    const response = await fetchWithCartToken('/cart/update-customer', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`Update customer failed: ${response.status}`);
    return response.json();
}

export async function submitCheckout(data: CheckoutRequest): Promise<CheckoutResponse> {
    const response = await fetchWithCartToken('/checkout', {
        method: 'POST',
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error((error as { message?: string })?.message ?? 'Checkout failed. Please try again.');
    }
    return response.json();
}

export interface PaymentMethodResponse {
    payment_method: string;
    fees: Array<{ name: string; total: string; tax: string }>;
    totals: { total_price: string; total_tax: string; total_fees: string };
    currency_code: string;
    currency_minor_unit: number;
}

export async function selectPaymentMethod(method: string): Promise<PaymentMethodResponse> {
    const restUrl = window.altcheckoutConfig?.restUrl ?? '/wp-json/';
    const response = await fetch(`${restUrl}altcheckout/v1/select-payment-method`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.altcheckoutConfig?.wpRestNonce ?? '',
        },
        credentials: 'include',
        body: JSON.stringify({ payment_method: method }),
    });
    if (!response.ok) throw new Error(`Select payment method failed: ${response.status}`);
    return response.json();
}

export async function checkEmail(email: string): Promise<{ exists: boolean }> {
    const restUrl = window.altcheckoutConfig?.restUrl ?? '/wp-json/';
    const response = await fetch(
        `${restUrl}altcheckout/v1/check-email?email=${encodeURIComponent(email)}`,
        { credentials: 'include' }
    );
    if (!response.ok) throw new Error(`Email check failed: ${response.status}`);
    return response.json();
}

export interface OrderDetailsResponse {
    order_id: number;
    status: string;
    is_paid: boolean;
    date_created: string;
    items: Array<{
        name: string;
        quantity: number;
        total: number;
        image: string;
        product_id: number;
    }>;
    downloads: Array<{
        name: string;
        file: string;
        url: string;
    }>;
    totals: {
        subtotal: number;
        discount: number;
        tax: number;
        total: number;
    };
    payment_method: string;
    payment_method_title: string;
    currency_code: string;
    currency_symbol: string;
    billing_email: string;
    get_started_url: string;
}

export async function getOrderDetails(orderId: string | number, key: string): Promise<OrderDetailsResponse> {
    const restUrl = window.altcheckoutConfig?.restUrl ?? '/wp-json/';
    const response = await fetch(
        `${restUrl}altcheckout/v1/order?order_id=${orderId}&key=${encodeURIComponent(key)}`,
        { credentials: 'include' }
    );
    if (!response.ok) {
        const error = await response.json();
        throw new Error((error as { error?: string })?.error ?? 'Failed to load order details.');
    }
    return response.json();
}

export async function loginUser(email: string, password: string): Promise<{ success: boolean; user_id?: number }> {
    const restUrl = window.altcheckoutConfig?.restUrl ?? '/wp-json/';
    const response = await fetch(`${restUrl}altcheckout/v1/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': window.altcheckoutConfig?.wpRestNonce ?? '',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error((error as { message?: string })?.message ?? 'Login failed.');
    }
    return response.json();
}
